/**
 * Rows for the Financing and approval tab (BAR-2829): one per Patient in the
 * stage, with what they're waiting on.
 */

import { getReferenceString } from '@medplum/core';
import { Patient, Practitioner } from '@medplum/fhirtypes';
import { ActiveLeadState } from '@bariendo/lib/active-lead';
import { getCareTeamMembersInPool } from '@bariendo/lib/care-team-roles';
import { BariatricProcedureStage, BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM, Track } from '@bariendo/lib/constants';
import { readEligibilityIntakeSelections, readEligibilityPayment } from '@bariendo/lib/eligibility-intake';
import { getTrackOrSelfPay } from '@bariendo/lib/episode-of-care';
import { PROCEDURE_STAGE_CHANGED_AT_EXTENSION } from '@bariendo/lib/extensions';
import { readInterestedService, readMarketLocation } from '@bariendo/lib/intake-selection';
import { parsePatientAccessCallAnswers } from '@bariendo/lib/pac-call-notes';
import { readEmployer } from '@bariendo/lib/pac-inquiry-list';
import { getStoredPaymentStatus, PaymentStatusResult } from '@bariendo/lib/payment-status';
import { isWithinListRange, ListRange } from '@/components/Hub/listRange';
import { PacQueueHydration } from '../hooks/usePacQueueHydration';
import {
  activeLeadFor,
  hydratedAppointments,
  LastCommunicationCell,
  StickyNoteCell,
  toLastCommunicationCell,
  toStickyNoteCell,
} from './listRowShared';

export interface FinancingLeadRow {
  id: string;
  patient: Patient;
  /** The status the stage bot stored (BAR-3084). Absent when the user can't see payments. */
  paymentStatus?: PaymentStatusResult;
  patientNavigatorPractitioners: Practitioner[];
  location?: string;
  procedure?: string;
  track?: Track;
  employer?: string;
  lastCommunication?: LastCommunicationCell;
  stickyNote?: StickyNoteCell;
  activeLead: ActiveLeadState;
}

function stageChangedAt(patient: Patient): string | undefined {
  return patient.extension?.find((entry) => entry.url === PROCEDURE_STAGE_CHANGED_AT_EXTENSION)?.valueDateTime;
}

function inFinancing(patient: Patient): boolean {
  return Boolean(
    patient.meta?.tag?.some(
      (tag) =>
        tag.system === BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM && tag.code === BariatricProcedureStage.FINANCING_APPROVAL
    )
  );
}

/** `entered` trims the `stage-day` tags' UTC edges to the exact instant; unstamped patients show only under All time. */
export function buildFinancingRows(
  patients: Patient[],
  hydration: PacQueueHydration,
  now: Date,
  entered: ListRange = {},
  /** `canViewPayments`: the status is read off the Patient, so the gate is here (BAR-3084). */
  showPaymentStatus = false,
  /** The tab's own scope; To schedule and Coverage reach Pre-procedure too (BAR-3107). */
  inScope: (patient: Patient) => boolean = inFinancing
): FinancingLeadRow[] {
  const rows: FinancingLeadRow[] = [];
  const bounded = Boolean(entered.from || entered.to);

  for (const patient of patients) {
    const patientRef = patient.id ? getReferenceString(patient) : undefined;
    // The tab's scope, as its query filters on; this catches rows that arrive by subscription.
    if (!patient.id || !patientRef || !inScope(patient)) {
      continue;
    }
    if (bounded && !isWithinListRange(stageChangedAt(patient), entered)) {
      continue;
    }

    const callNote = hydration.callNotesByPatient.get(patientRef)?.[0];
    const callAnswers = callNote ? parsePatientAccessCallAnswers(callNote) : undefined;
    const eligibility = hydration.eligibilityByPatient.get(patientRef);
    const episode = hydration.episodeByPatient.get(patientRef);
    const navigatorRefs = getCareTeamMembersInPool(
      hydration.careTeamByPatient.get(patientRef),
      hydration.patientNavigatorPool
    );
    const lastCommunication = hydration.lastCommunicationByPatient.get(patientRef);

    rows.push({
      id: patient.id,
      patient,
      paymentStatus: showPaymentStatus ? getStoredPaymentStatus(patient) : undefined,
      patientNavigatorPractitioners: navigatorRefs
        .map((reference) => hydration.practitionerByRef.get(reference))
        .filter((practitioner): practitioner is Practitioner => practitioner !== undefined),
      location: readMarketLocation(patient, callAnswers, readEligibilityIntakeSelections(eligibility).market),
      procedure: readInterestedService(patient, callAnswers, readEligibilityIntakeSelections(eligibility).procedure),
      track: getTrackOrSelfPay(episode),
      employer: readEmployer(patient) ?? readEligibilityPayment(eligibility).employer,
      lastCommunication: toLastCommunicationCell(lastCommunication),
      stickyNote: toStickyNoteCell(hydration.stickyNoteByPatient.get(patientRef), hydration.practitionerByRef),
      activeLead: activeLeadFor({
        lastCommunicationSent: lastCommunication?.sent,
        leadFlags: hydration.flagsByPatient.get(patientRef),
        signupAt: episode?.period?.start,
        appointments: hydratedAppointments(hydration, patientRef),
        now,
      }),
    });
  }

  return rows;
}
